<template>
  <div id="presentation-root" :class="pageClass">
    <editor-wizard add-class="gp-has-header color-light" v-if="showWizard" :group="currentGroup" @cancel="showWizard=false" @pageGenerated="wizardDone"/>

    <mdb-mask v-if="!showWizard" :class="maskClass" :overlay="overlay">
      <mdb-container class="text-left">
        <div v-if="currentGroup">
          <banner-small :group="currentGroup" title="Sumato Presentations"/>
        </div>
        <mdb-card v-if="sessions && sessions.length > 0 && !currentFolder" class="mb-4">
          <mdb-card-header class="primary-color text-white">
            <h3>Sessions in Progress</h3>
          </mdb-card-header>
          <mdb-card-body>
            <div v-for="session in sessions" :key="session.id">
              <div style="clear:both;">
                <avatar :avatar="session.hostAvatar" :name="session.host"/>
                <b>{{ session.name }}</b>,
                hosted by {{ session.host }}
              </div>
              <mdb-btn icon="play" rounded size="sm" @click="$router.push('/presentation-viewer/' + session.sessionId)">
                Join
              </mdb-btn>
            </div>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="sessions && sessions.length == 0 && !allowEdit" class="mb-4 text-center">
          <mdb-card-body>
            <sumato/>
            <h3>Sorry, but no sessions are currently in progress</h3>
          </mdb-card-body>
        </mdb-card>

        <div v-if="currentGroup && allowEdit && !currentPresentation">
          <mdb-card>
            <mdb-card-body>
              <div>
                <mdb-breadcrumb class="breadcrumb-with-arrows">
                  <mdb-breadcrumb-item v-for="(bc,i) in folderBreadcrumbs" :key="i">
                    <span v-if="folderBreadcrumbs.length == i+1">{{ bc.name }}</span>
                    <a v-if="i+1 < folderBreadcrumbs.length" href="" @click.prevent="folderNavUp(bc)">{{ bc.name }}</a>
                  </mdb-breadcrumb-item>
                </mdb-breadcrumb>
                <div v-if="allowEdit && !currentPresentation" class="mb-2">
                  <mdb-btn color="success" icon="folder-plus" size="sm" @click="createFolderModal = true">
                    New Folder
                  </mdb-btn>
                  <mdb-btn v-if="currentFolder" size="sm" @click="openRename">
                    <mdb-icon icon="pencil-alt"/>
                    Rename Folder
                  </mdb-btn>
                  <mdb-btn v-if="presentations.length==0 && folders.length == 0 && currentFolder"
                           color="danger"
                           icon="trash"
                           size="sm" tag="a" @click="deleteFolderModal = true">
                    Delete Folder
                  </mdb-btn>
                  <mdb-btn v-if="currentFolder" size="sm" @click="moveFolder">
                    <mdb-icon icon="folder-tree"/>
                    Move Folder
                  </mdb-btn>

                </div>
                <mdb-card v-if="folders.length>0" class="mb-4">
                  <mdb-card-body>
                    <div v-for="folder in folders" v-bind:key="folder.id">
                      <a role="button" @click="selectFolder(folder)">
                        <mdb-icon color="primary" far icon="folder" size="2x"/>
                        <span class="text-secondary">{{ folder.name }}</span>
                      </a>
                    </div>

                  </mdb-card-body>
                </mdb-card>
              </div>

              <mdb-card v-if="currentFolder && allowEdit && !currentPresentation">
                <mdb-card-header class="primary-color text-white">
                  <h4>Presentations</h4>
                </mdb-card-header>
                <mdb-card-body>
                  <div>
                    <mdb-btn color="success" size="sm" @click="createPresentationModal = true">
                      <mdb-icon icon="presentation"/>
                      New Presentation
                    </mdb-btn>
                    <mdb-btn color="success" size="sm" @click="showUploadModal = true">
                      <mdb-icon icon="upload"/>
                      Upload a Presentation
                    </mdb-btn>
                  </div>
                  <div v-if="presentations.length == 0" class="text-info">
                    Folder is empty.
                  </div>
                  <mdb-tbl v-if="presentations.length >0" responsive striped>
                    <mdb-tbl-head class="secondary-color text-white">
                      <tr>
                        <th><h5>Name</h5></th>
                        <th>&nbsp;</th>
                      </tr>
                    </mdb-tbl-head>
                    <mdb-tbl-body v-if="presentations.length>0">
                      <tr v-for="pres in presentations" :key="pres.id">
                        <td>{{ pres.name }}</td>
                        <td class="text-right">
                          <mdb-btn floating icon="pencil" size="sm"
                                   tag="a"
                                   title="Edit" @click="editPresentation(pres)"/>
                          <mdb-btn floating icon="download" size="sm"
                                   tag="a"
                                   title="Export" @click="exportPresentation(pres)"/>
                          <mdb-btn floating icon="file-pdf" size="sm"
                                   tag="a"
                                   title="Generate as PDF" @click="pdf(pres)"/>
                          <mdb-btn color="warning" floating icon="folder-tree" size="sm" tag="a"
                                   title="Move to another folder."
                                   @click="movePresentation(pres)"/>
                          <mdb-btn color="danger" floating icon="trash" size="sm" tag="a"
                                   title="Delete"
                                   @click="deletePresentation(pres)"/>
                          <mdb-btn floating icon="play" size="sm" tag="a"
                                   title="Start a presentation."
                                   @click="play(pres)"/>
                        </td>
                      </tr>
                    </mdb-tbl-body>
                  </mdb-tbl>
                </mdb-card-body>
              </mdb-card>
            </mdb-card-body>
          </mdb-card>
        </div>

        <div v-if="currentPresentation && allowEdit && !workingPage">
          <mdb-btn icon="arrow-left" size="sm" @click="presentationList">Back to {{ currentFolder.name }}</mdb-btn>
          <mdb-card>
            <mdb-card-header class="primary-color text-white">
              <h3>
                {{ currentPresentation.name }}
                <mdb-btn floating icon="pencil" size="sm" tag="a"
                         @click="editPresentationDetails"/>
                <mdb-btn floating icon="camera" size="sm" tag="a" @click="backgroundModal = true"/>
                <mdb-btn floating icon="play" size="sm" tag="a" @click="play(currentPresentation)"/>
              </h3>
              <div v-if="pages.length>1 && !currentPresentation.randomize" class="text-white">
                Drag a page title to re-order presentation order.
              </div>
            </mdb-card-header>
            <mdb-card-body v-if="workingPresentation">
              <mdb-input v-model="workingPresentation.name" label="Name" placeholder="Name"/>
              <mdb-input id="randomize" v-model="workingPresentation.randomize" label="Randomize order on presentation?"
                         name="randomize"
                         type="checkbox"/>
              <mdb-btn :disabled="!workingPresentation.name" color="success" icon="save"
                       @click="savePresentationDetails">Save
              </mdb-btn>
              <mdb-btn color="warning" icon="times-circle" @click="workingPresentation = null">Cancel</mdb-btn>
            </mdb-card-body>
            <mdb-card-body v-if="!workingPresentation">

              <div v-if="pages.length == 0" class="text-info">
                <mdb-btn icon="file-plus" @click="newPage(null)">Create First Page</mdb-btn>
              </div>
              <mdb-tbl responsive striped>
                <mdb-tbl-head class="secondary-color text-white">
                  <tr>
                    <th class="h5">Name</th>
                    <th>Type</th>
                    <th>&nbsp;</th>
                  </tr>
                </mdb-tbl-head>

                <draggable v-if="pages.length >0" :list="pages" tag="tbody" @change="reorderPage">
                  <tr v-for="page in pages" :id="'page-row-' + page.id" :key="page.id">
                    <td>{{ page.name }}</td>
                    <td>
                      <mdb-badge>{{ page.type }}</mdb-badge>
                    </td>
                    <td class="text-right">
                      <mdb-btn floating icon="pencil" size="sm" tag="a" @click="editPage(page)"/>
                      <mdb-btn floating icon="copy" size="sm" tag="a" title="Clone this page" @click="clonePage(page)"/>
                      <mdb-btn floating icon="file-plus" size="sm" tag="a" title="Add a page after this one."
                               @click="newPage(page)"/>
                      <mdb-btn color="danger" floating icon="trash" size="sm" tag="a" @click="deletePage(page)"/>
                    </td>
                  </tr>
                </draggable>

              </mdb-tbl>
            </mdb-card-body>
          </mdb-card>
        </div>

        <div v-if="currentPresentation && allowEdit && workingPage">
          <mdb-btn icon="arrow-left" rounded size="sm" @click="doneEditingPage">Back</mdb-btn>
          <mdb-btn v-if="workingPage.type != 'YT'" icon="eye" rounded size="sm" @click="preview">Preview</mdb-btn>
          <mdb-card>
            <mdb-card-header class="secondary-color text-white">
              <h3>{{ currentPage.name }}</h3>
            </mdb-card-header>
            <mdb-card-body>

              <mdb-input v-model="workingPage.name" label="Page Name"/>
              <gp-select :dataObj="workingPage" :possible-values="pageTypes" :required="true"
                         label="Type" propertyName="type"
              />
              <div v-if="workingPage.type=='YT'">
                <mdb-input v-model="workingPage.youTubeId" label="YouTube Video ID"/>
              </div>
              <div v-if="workingPage.type=='EV'">
                <div>
                  <label class="formless-label">Video</label>
                </div>
                <div v-if="workingPage.ezumeeVideo">{{ workingPage.ezumeeVideo.name }}</div>
                <mdb-btn icon="video" rounded size="sm" @click="videoNavigator = true">
                  {{ workingPage.ezumeeVideo ? 'Change Video' : 'Select Video' }}
                </mdb-btn>
              </div>
              <div v-if="workingPage.type != 'YT' && workingPage.type != 'EV'" class="pt-2">
                <div class="mb-4 border">
                  <div class="formless-label">Page Body</div>
                  <div id="text-wizard-area" class="grey lighten-5 border">
                    <editor v-model="workingPage.text" :init="editorConfig"/>
                  </div>
                  <div style="clear:both;"/>
                  <mdb-btn icon="hat-wizard" rounded size="sm" @click="startWizard('text')">Use Page Wizard</mdb-btn>
                </div>

                <div v-if="workingPage.type == 'QC' || workingPage.type == 'IC'" class="pt-2">
                  <label>Multiple Choice Values</label>
                  <cust-chip-input :chips="workingPage.choiceAnswers"/>
                </div>
                <div v-if="workingPage.type == 'SR'" class="pt-2 border">
                  <div class="formless-label">Reveal Text</div>
                  <div id="reveal-wizard-area" class="grey lighten-5 border">
                    <editor v-model="workingPage.revealText" :init="editorConfig"/>
                  </div>
                  <div style="clear:both;"/>
                  <mdb-btn icon="hat-wizard" rounded size="sm" @click="startWizard('reveal')">Use Page Wizard</mdb-btn>
                </div>
              </div>
            </mdb-card-body>
            <mdb-card-footer>
              <mdb-btn :disabled="!workingPage.name" color="success" icon="save" @click="savePage">Save</mdb-btn>
              <mdb-btn color="warning" icon="times-circle" @click="doneEditingPage">Cancel</mdb-btn>
            </mdb-card-footer>
          </mdb-card>
        </div>
        <mdb-modal :show="movePresentationModal" class="text-left" size="lg" @close="movePresentationModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>Move to Folder</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <group-presentation-navigator v-if="currentGroup"
                                          :exclude-folder="currentFolder" :group="currentGroup.id"
                                          folders-only @selectedFolder="doMovePresentation"/>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="currentFolder" :show="moveFolderModal" class="text-left" size="lg"
                   @close="moveFolderModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>Move {{ currentFolder.name }} to....</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <group-presentation-navigator v-if="currentGroup" :exclude-folder="currentFolder"
                                          :group="currentGroup.id" allow-root
                                          folders-only @selectedFolder="doMoveFolder"/>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal :show="createFolderModal" class="text-center" size="lg" @close="createFolderModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>New Folder</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="newFolderName"
                       v-model="newFolderName" active label="Name"
                       @keydown.native="newNameKeyDown"
            />
            <mdb-btn @click="createFolder">
              <mdb-icon icon="save"/>
              Save
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal :show="createPresentationModal" size="lg" @close="createPresentationModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>New Presentation</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="newPresentationName" v-model="newPresentationName"
                       active
                       label="Name" @keydown.native="newPresentationNameKeyDown"/>
            <mdb-btn @click="createPresentation">
              <mdb-icon icon="save"/>
              Save
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal :show="createPageModal" size="lg" @close="createPageModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>New Page</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="newPageName" v-model="newPageName"
                       active
                       label="Name" @keydown.native="newPageNameKeyDown"/>
            <mdb-btn @click="createPage">
              <mdb-icon icon="save"/>
              Save
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal :show="renameModal" size="lg" @close="renameModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3 v-if="currentFolder">Rename {{ currentFolder.name }} </h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="renameFolderName" v-model="newFolderName"
                       active
                       label="New Name" @keydown.native="renameKeyDown"/>
            <mdb-btn @click="renameFolder">
              <mdb-icon icon="save"/>
              Save
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>
        <yes-no-modal v-if="showWizardAck" additional-text="Existing page text will be overwritten."
                      @no="showWizardAck = false"
                      @yes="wizardAckDone"/>
        <yes-no-modal v-if="deleteFolderModal" @no="deleteFolderModal=false" @yes="deleteFolder"/>
        <yes-no-modal v-if="deletePresentationModal" @no="deletePresentationModal=false" @yes="doDeletePresentation"/>
        <yes-no-modal v-if="deletePageModal" @no="deletePageModal=false" @yes="doDeletePage"/>
        <mdb-modal v-if="backgroundModal" size="lg" @close="backgroundModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>Set Background Image for Pages</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <background-chooser reference-only type="group" @newBackground="changeBackground"/>
          </mdb-modal-body>
        </mdb-modal>
        <mdb-modal v-if="previewHtml" size="lg" @close="previewHtml=null">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>Preview</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <div v-html="previewHtml"/>
          </mdb-modal-body>
        </mdb-modal>
        <mdb-modal v-if="playModal" size="lg" @close="playModal=false">
          <mdb-modal-header :close="true" center class="secondary-color text-white">
            <h3>Play Presentation</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="guests" v-model="newSessionConfig.allowGuests"
                       label="Allow Guests?"
                       name="guests" type="checkbox"/>
            <mdb-input id="requirePassword" v-model="newSessionConfig.passwordRequired" label="Require Password?"
                       name="requirePassword"
                       type="checkbox"/>
            <mdb-input v-if="newSessionConfig.passwordRequired" id="password" v-model="newSessionConfig.password"
                       label="Password"
                       name="password" type="text"/>
            <mdb-btn icon="play" @click="startPresentation">Start</mdb-btn>
          </mdb-modal-body>
        </mdb-modal>
        <mdb-modal :show="showImageSelector" @close="showImageSelector = false" class="gp-z-2000">
          <mdb-modal-header :close="true" center>Select an Image
          </mdb-modal-header>
          <mdb-modal-body>
            <image-selector :group="currentGroup" emitOnSelection="imageSelected" @imageSelected="imageSelected"/>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="videoNavigator" size="lg" @close="videoNavigator = false">
          <mdb-modal-header :close="true" class="primary-color text-white">Select a Video
          </mdb-modal-header>
          <mdb-modal-body>
            <data-table :clickHandler="pickedVideo"
                        :columns="columns"
                        dataSource="/video/list"/>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showUploadModal" class="animated"
                   info position="top-left"
                   side @close="showUploadModal = false">
          <mdb-modal-header>
            <p class="heading">Upload a File to Import</p>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-file-input v-on:getValue="importPresentation"/>
          </mdb-modal-body>
        </mdb-modal>
      </mdb-container>
    </mdb-mask>
  </div>

</template>
<script>
import draggable from "vuedraggable"
import api from '../../services/groupsPlaceApi.js'
import bannerSmall from '../group/BannerSmall'
import error from '../common/Error'
import Vue from 'vue';
import YesNoModal from "../common/YesNoModal";
import BackgroundChooser from "../group/BackgroundChooser";
import editor from '@tinymce/tinymce-vue'
import ImageSelector from "../group/ImageSelector";
import Avatar from "../common/Avatar";
import gpSelect from "../common/GPSelect";
import download from 'js-file-download';
import {custChipInput} from "../common/CustChipInput";
import Sumato from "../Sumato";
import GroupPresentationNavigator from "./Navigator";
import dataTable from "../common/DataTable";
import EditorWizard from "../common/EditorWizard";
import editorSettings from "../../services/editorSettings";

import {
  mdbBadge,
  mdbBreadcrumb,
  mdbBreadcrumbItem,
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbCardImage,
  mdbCardText,
  mdbCardTitle,
  mdbCol,
  mdbContainer,
  mdbFileInput,
  mdbIcon,
  mdbInput,
  mdbListGroup,
  mdbListGroupItem,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbRow,
  mdbTbl,
  mdbTblBody,
  mdbTblHead,
  mdbTooltip,
  mdbTreeview,
  mdbTreeviewItem,
  mdbView
} from 'mdbvue';

export default {
  name: "group-presentation-library",
  display: "Presentation Library",
  order: 1,
  watch: {
    createPresentationModal: function (n, o) {
      if (n) this.focusElement("newPresentationName")
    },
    createPageModal: function (n, o) {
      if (n) this.focusElement("newPageName")
    },
    createFolderModal: function (n, o) {
      if (n) this.focusElement("newFolderName")
    },
    renameModal: function (n, o) {
      if (n) this.focusFirstElement("renameFolderName")
    }
  },
  components: {
    GroupPresentationNavigator,
    mdbContainer,
    mdbBtn,
    mdbRow,
    mdbCol,
    mdbListGroup,
    mdbListGroupItem,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardTitle,
    mdbCardText,
    mdbCardFooter,
    mdbCardImage,
    mdbMask,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbIcon,
    mdbTreeview,
    mdbTreeviewItem,
    mdbBadge,
    mdbView,
    mdbTooltip,
    mdbInput,
    mdbBreadcrumb,
    mdbBreadcrumbItem,
    mdbTbl,
    mdbTblBody,
    mdbTblHead,
    draggable,
    bannerSmall,
    error,
    YesNoModal,
    BackgroundChooser,
    mdbFileInput,
    editor,
    ImageSelector,
    Avatar,
    gpSelect,
    custChipInput,
    Sumato,
    dataTable,
    EditorWizard
  },
  computed: {
    currentGroup() {
      return this.$store.state.currentGroup
    },
    allowEdit() {
      if (!this.currentGroup) return false
      if (this.currentGroup.role == "Admin" || this.currentGroup.role == "Event Organizer" || this.currentGroup.role == "Group Organizer") return true
      return false
    },
    settings() {
      return this.$store.state.settings
    }
  },
  mounted() {
    this.editorConfig.file_picker_callback = this.myFileBrowser
    this.editorConfig.paste_data_images = true
    this.editorConfig.images_upload_handler = this.handlePastedImage
    this.loadFolders(false)
  },
  data() {
    return {
      folders: [],
      presentations: [],
      error: null,
      currentFolder: null,
      currentPresentation: null,
      playPresentation: null,
      pages: [],
      currentPage: null,
      workingPresentation: null,
      workingPage: null,
      createFolderModal: false,
      createPresentationModal: false,
      columns: [
        {label: "ID", field: "id"},
        {label: "Name", field: "name"}
      ],
      createPageModal: false,
      deleteFolderModal: false,
      deletePresentationModal: false,
      deletePageModal: false,
      renameModal: false,
      playModal: false,
      wizardField: null,
      listScrollPos: 0,
      wizardAcknowledged: false,
      newFolderName: "",
      newPresentationName: "",
      newPageName: "",
      showRenameList: false,
      moveCandidate: null,
      deleteCandidate: null,
      pageClass: null,
      previewHtml: null,
      maskClass: "",
      createAfterPage: null,
      overlay: "",
      backgroundModal: false,
      showImageSelector: false,
      isOrganizer: false,
      movePresentationModal: false,
      showUploadModal: false,
      moveFolderModal: false,
      folderBreadcrumbs: [{id: 0, name: "Top"}],
      videoNavigator: false,
      sessions: [],
      pageTypes: [],
      showWizard: false,
      showWizardAck: false,
      newSessionConfig: {
        allowGuests: true,
        passwordRequired: false,
        password: ""
      },
      editorConfig: editorSettings.theWorksInline()
    };
  },
  methods: {
    focusElement(elName) {
      Vue.nextTick().then(function () {
        let el = document.getElementById(elName)
        if (el) el.focus()
      })
    },
    doneEditingPage() {
      this.workingPage = null
      Vue.nextTick().then(() => {
        window.scrollTo(0, this.listScrollPos)
      })
    },
    newNameKeyDown(e) {
      if (e.keyCode == 13 && this.newFolderName) this.createFolder()
    },
    renameKeyDown(e) {
      if (e.keyCode == 13 && this.newFolderName) this.renameFolder()
    },
    newPageNameKeyDown(e) {
      if (e.keyCode == 13 && this.newPageName) this.createPage()
    },
    newPresentationNameKeyDown(e) {
      if (e.keyCode == 13 && this.newPresentationName) this.createPresentation()
    },
    startWizard(field) {
      this.wizardField = field
      if (this.wizardField == "text" && this.workingPage.text) this.showWizardAck = true
      if (this.wizardField == "reveal" && this.workingPage.revealText) this.showWizardAck = true
      if (!this.showWizardAck) this.showWizard = true
    },
    wizardAckDone() {
      this.showWizardAck = false
      this.showWizard = true
    },
    wizardDone(result) {
      this.showWizard = false
      if (this.wizardField == "text") {
        this.workingPage.text = result.html
      }
      if (this.wizardField == "reveal") {
        this.workingPage.revealText = result.html
      }
    },
    preview() {
      this.$store.dispatch("setExecuting", true)
      let html = ""
      if (this.workingPage.text) html += "<div>" + this.workingPage.text + "</div>"
      if (this.workingPage.revealText) html += "<div>" + this.workingPage.revealText + "</div>"
      api.post("/user/sanitizer", {pageText: html}).then(
        (results) => {
          this.previewHtml = results.data.pageText
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    newPage(afterPage) {
      this.createAfterPage = afterPage
      this.createPageModal = true
      if (afterPage) {
        let el = document.getElementById("page-row-" + afterPage.id)
        if (el) this.listScrollPos = el.offsetTop
      }
      this.$nextTick(function () {
        let el = document.getElementById("newPageName")
        if (el) el.focus()
      })
    },
    play(presentation) {
      this.playPresentation = presentation
      this.playModal = true
    },
    openRename() {
      this.newFolderName = this.currentFolder.name
      this.renameModal = true
    },
    startPresentation() {
      this.playModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/presentation/start/" + this.playPresentation.id, this.newSessionConfig).then(
        (results) => {
          this.$router.push("/presentation-viewer/" + results.data.sessionId)
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    loadFolders(forceRoot) {
      if (forceRoot) this.folderBreadcrumbs = [{id: 0, name: "Top"}]
      this.$store.dispatch("setExecuting", true)
      let url = "/presentation/folders/" + this.$route.params.id + "/?root=" + (forceRoot ? 'y' : 'n')
      api.get(url).then(
        (results) => {
          this.pageTypes = results.data.pageTypes
          this.isOrganizer = results.data.isOrganizer
          this.sessions = results.data.sessions
          if (results.data.lastFolder) {
            this.folders = results.data.lastFolder.subFolders
            this.currentFolder = results.data.lastFolder.folder
            this.presentations = results.data.lastFolder.presentations
            this.folderBreadcrumbs = results.data.lastFolder.breadcrumbs
          } else this.folders = results.data.folders
          if (!this.currentGroup || this.currentGroup.id != results.data.groupId) api.groupBasics(this.$route.params.id, "member");
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    editPresentation: function (pres) {
      this.currentPresentation = pres
      this.setBackground()
      this.loadPages()
    },
    editPresentationDetails() {
      this.workingPresentation = JSON.parse(JSON.stringify(this.currentPresentation))
    },
    movePresentation(presentation) {
      this.moveCandidate = presentation
      this.movePresentationModal = true
    },
    moveFolder(folder) {
      this.moveFolderModal = true
    },
    doMovePresentation(newFolder) {
      this.movePresentationModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/presentation/move/" + this.moveCandidate.id, {newFolder}).then(
        (details) => {
          let loc = this.presentations.indexOf(this.moveCandidate)
          this.presentations.splice(loc, 1)
          this.$notify.success({"message": "Presentation moved to " + newFolder.name + ".", timeOut: 3000})
          this.moveCandidate = null
        },
        (error) => {
          this.$notify.error({message: "Error moving folder"})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    doMoveFolder(newFolder) {
      this.moveFolderModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/presentation/moveFolder/" + this.currentFolder.id, {newFolder}).then(
        (details) => {
          if (newFolder) this.selectFolder(newFolder)
          else this.loadFolders(true)
        },
        (error) => {
          this.$notify.error({message: "Error moving folder"})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    displayTypeValue(code) {
      let ret = ""
      for (let t of this.pageTypes) {
        if (t.value == code) ret = t.text
      }
      return ret

    },
    savePage() {
      this.$store.dispatch("setExecuting", true)
      if (this.workingPage.choiceAnswers && this.workingPage.choiceAnswers.length > 0) {
        this.workingPage.flattenedAnswers = this.workingPage.choiceAnswers.join()
      } else this.workingPage.flattenedAnswers = ""
      api.post("/presentation/pageDetails/" + this.currentPage.id, this.workingPage).then(
        () => {
          this.currentPage.name = this.workingPage.name
          this.currentPage.type = this.displayTypeValue(this.workingPage.type)
          this.$notify.success({message: "Page Saved", timeOut: 2000})
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    savePresentationDetails() {
      this.$store.dispatch("setExecuting", true)
      api.post("/presentation/details/" + this.currentPresentation.id, this.workingPresentation).then(
        () => {
          this.currentPresentation.name = this.workingPresentation.name
          this.currentPresentation.randomize = this.workingPresentation.randomize
          this.workingPresentation = null
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    exportPresentation(presentation) {
      this.$store.dispatch("setExecuting", true)
      api.get("/presentation/export/" + presentation.id).then(
        (res) => {
          download(JSON.stringify(res.data), res.data.name + ".json")
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    pdf(presentation) {
      this.$store.dispatch("setExecuting", true)
      api.postBlob("/presentation/pdf/" + presentation.id, {}).then(
        (res) => {
          download(res.data, presentation.name + ".pdf")
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    importPresentation: function (file) {
      this.showUploadModal = false
      this.$store.dispatch("setExecuting", true)
      api.postFile("/presentation/importFile/" + this.currentFolder.id, file[0]).then(
        (results) => {
          this.$notify.success({
                                 message: "Presentation uploaded successfully.",
                                 timeOut: 3000
                               })
          this.presentations.unshift(results.data)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },

    changeBackground(img) {
      this.backgroundModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/presentation/background/" + this.currentPresentation.id, {image: img.id}).then(
        () => {
          this.currentPresentation.background = img.id
          this.currentPresentation.backgroundUrl = img.url
          this.setBackground()
        },
        (error) => {
          this.backgroundModal = true
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    setBackground: function () {
      let img
      if (this.currentPresentation && this.currentPresentation.backgroundUrl) {
        img = this.currentPresentation.backgroundUrl
        this.pageClass = "full-view";
        this.maskClass = "overflow-auto gp-full-overlay";
        this.overlay = "stylish-strong";
      } else {
        this.maskClass = "";
        this.overlay = "";
      }
      Vue.nextTick().then(() => {
        let el = document.getElementById("presentation-root");
        if (el) {
          if (img) {
            el.setAttribute("style", "background-image: url('" + img + "');")
          } else {
            el.removeAttribute("style");
            this.pageClass = ""
          }
        }
      })
    },
    reorderPage(evt) {
      if (evt && evt.moved) {
        let payload = []
        let sequence = 0
        for (let p of this.pages) {
          payload.push(p.id)
        }
        this.$store.dispatch("setExecuting", true)
        api.post("/presentation/reorder/" + this.currentPresentation.id, payload).then(
          () => {

          },
          (error) => {
            this.$notify.error({message: error.response.data})
          }).then(() => {
          this.$store.dispatch("setExecuting", false)
        })
      }

    },
    loadPages() {
      this.$store.dispatch("setExecuting", true)
      this.pages = []
      api.get("/presentation/pages/" + this.currentPresentation.id).then(
        (results) => {
          this.pages = results.data
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    presentationList() {
      this.currentPresentation = null
      this.pages = []
      this.setBackground()
    },
    renameFolder: function () {
      if (!this.newFolderName || !this.newFolderName.trim()) {
        this.$notify.error({message: "Folder name cannot be empty."})
        return
      }
      this.renameModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/presentation/renameFolder/" + this.currentFolder.id, {name: this.newFolderName}).then(
        (details) => {
          this.currentFolder.name = this.newFolderName
          this.folderBreadcrumbs[this.folderBreadcrumbs.length - 1].name = this.newFolderName
          this.newFolderName = ""
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
          this.renameModal = true
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    createFolder: function () {
      if (!this.newFolderName || !this.newFolderName.trim()) {
        this.$notify.error({message: "Folder name cannot be empty."})
        return
      }
      this.createFolderModal = false
      this.$store.dispatch("setExecuting", true)
      api.put("/presentation/createFolder/" + this.currentGroup.vanityPath, {
        name: this.newFolderName,
        parent: this.folderBreadcrumbs.length == 1 ? null : this.currentFolder
      }).then(
        (details) => {
          this.folders.push(details.data)
          this.newFolderName = ""
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
          this.createFolderModal = true
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    createPresentation: function () {
      if (!this.newPresentationName || !this.newPresentationName.trim()) {
        this.$notify.error({message: "Presentation name cannot be empty."})
        return
      }
      this.createPresentationModal = false
      this.$store.dispatch("setExecuting", true)
      api.put("/presentation/create", {
        name: this.newPresentationName,
        folder: this.currentFolder.id
      }).then(
        (details) => {
          this.presentations.unshift(details.data)
          this.currentPresentation = this.presentations[0]
          this.newPresentationName = ""
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
          this.createPresentationModal = true
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    clonePage: function (page) {
      this.$store.dispatch("setExecuting", true)
      api.get("/presentation/clonePage/" + page.id).then(
        (results) => {
          let loc = this.pages.indexOf(page)
          this.pages.splice(loc + 1, 0, results.data)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    editPage: function (page) {

      let el = document.getElementById("page-row-" + page.id)
      if (el) this.listScrollPos = el.offsetTop - 40
      this.$store.dispatch("setExecuting", true)
      api.get("/presentation/pageDetails/" + page.id).then(
        (results) => {
          this.currentPage = page
          this.workingPage = results.data
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    createPage: function () {
      if (!this.newPageName || !this.newPageName.trim()) {
        this.$notify.error({message: "Page name cannot be empty."})
        return
      }

      this.createPageModal = false
      this.$store.dispatch("setExecuting", true)
      api.put("/presentation/createPage", {
        name: this.newPageName,
        createAfter: this.createAfterPage,
        presentation: this.currentPresentation.id
      }).then(
        (details) => {
          if (this.createAfterPage) {
            let loc = this.pages.indexOf(this.createAfterPage)
            this.pages.splice(loc + 1, 0, {id: details.data.id, name: details.data.name})
          } else this.pages.push({id: details.data.id, name: details.data.name})
          this.currentPage = details.data;
          this.newPageName = ""
          this.editPage(this.currentPage)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
          this.createPresentationModal = true
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deleteFolder: function () {
      this.deleteFolderModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/presentation/deleteFolder/" + this.currentFolder.id).then(
        (details) => {
          if (this.currentFolder.parent) {
            this.selectFolder({id: this.currentFolder.parent})
          } else {
            this.currentFolder = null
            this.loadFolders(true)
          }
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deletePresentation: function (p) {
      this.deleteCandidate = p
      this.deletePresentationModal = true
    },
    doDeletePresentation: function () {
      this.deletePresentationModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/presentation/delete/" + this.deleteCandidate.id).then(
        (details) => {
          let loc = this.presentations.indexOf(this.deleteCandidate)
          this.deleteCandidate = null
          this.presentations.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deletePage: function (p) {
      this.deleteCandidate = p
      this.deletePageModal = true
    },
    doDeletePage: function () {
      this.deletePageModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/presentation/deletePage/" + this.deleteCandidate.id).then(
        (details) => {
          let loc = this.pages.indexOf(this.deleteCandidate)
          this.deleteCandidate = null
          this.pages.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    folderNavUp(folder) {
      this.currentPresentation = null
      this.pages = []
      this.presentations = []
      if (folder.id == 0) {
        this.currentFolder = null
        this.loadFolders(true)
        return
      }
      this.selectFolder(folder)
    },
    pickedVideo: function (vid) {
      this.workingPage.ezumeeVideo = vid
      this.videoNavigator = false
    },
    selectFolder: function (folder) {
      this.$store.dispatch("setExecuting", true)
      let folderId = folder.id
      let url = "/presentation/list/" + folderId
      api.get(url).then(
        (details) => {
          this.presentations = details.data.presentations
          this.folders = details.data.subFolders
          this.currentFolder = details.data.folder
          this.folderBreadcrumbs = details.data.breadcrumbs
        },
        (error) => {
          this.$notify.error({message: "Error loading folder"})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    imageSelected: function (image) {
      this.showImageSelector = false
      let el = document.getElementsByClassName("tox-dialog-wrap")
      el[0].setAttribute("style", "display:block")
      Vue.nextTick().then(() => {
        this.imageCallback(image.url, {width: "33%", height: "", class: "booger-but"})
      })

    },
    myFileBrowser: function (callBack, value, meta) {
      let el = document.getElementsByClassName("tox-dialog-wrap")
      el[0].setAttribute("style", "display:none")
      Vue.nextTick().then(() => {
        this.showImageSelector = true
        this.imageCallback = callBack
      })
    },
    handlePastedImage: function(blobInfo, success, failure, progress) {
      let url = "/image/create/?group=" + this.currentGroup.vanityPath + "&folder=pasted"
      this.$store.dispatch("setExecuting", true)
      api.postImage(url, blobInfo.blob()).then(
        (results) => {
          success(results.data.image.url)
          let els = document.getElementsByClassName("mce-content-body")
          for (let el of els) {
            let images = el.getElementsByTagName("img")
            for (let i of images) {
              if (! i.hasAttribute("width")) {
                i.setAttribute("width", "33%");
                i.classList.add("gp-user-image-left");
              }
            }
          }
        },
        (error) => {
          failure(error.response.data, {remove: true})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
  }
};

</script>
<style scoped>
.no-padding {
  padding: 0 !important;
}

</style>


